import { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { ShieldLogo } from "../components/landing/Logo";
import { 
  Camera, Eye, AlertTriangle, CheckCircle2, Upload, 
  Loader2, Home, Shield, Utensils, Thermometer, 
  Users, Clock, Trash2, Zap, RefreshCw, XCircle,
  Volume2, Play, Image as ImageIcon, ChefHat
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Analysis type configurations
const ANALYSIS_TYPES = {
  plating: {
    name: "Plating Verification",
    description: "Analyze dish presentation and generate plating guide",
    icon: ChefHat,
    color: "gold"
  },
  inventory: {
    name: "Inventory Scan",
    description: "Detect stock levels, FIFO compliance, and restock needs",
    icon: Utensils,
    color: "blue"
  },
  spoilage: {
    name: "Spoilage Detection",
    description: "Check freshness and identify food safety risks",
    icon: AlertTriangle,
    color: "red"
  },
  guest_agitation: {
    name: "Guest Experience",
    description: "Monitor wait times and guest satisfaction indicators",
    icon: Users,
    color: "purple"
  },
  equipment: {
    name: "Equipment Monitor",
    description: "Check operational status and energy waste",
    icon: Thermometer,
    color: "cyan"
  },
  dish_pit: {
    name: "Dish Pit Intelligence",
    description: "Optimize dish flow and reduce waste",
    icon: Trash2,
    color: "green"
  }
};

// Alert severity colors
const SEVERITY_COLORS = {
  CRITICAL: "bg-red-500/20 border-red-500/50 text-red-400",
  WARNING: "bg-amber-500/20 border-amber-500/50 text-amber-400",
  INFO: "bg-blue-500/20 border-blue-500/50 text-blue-400"
};

// Privacy Notice Component
const PrivacyNotice = () => (
  <div className="glass-card rounded-lg p-4 border border-lime/30 bg-lime/5 mb-6">
    <div className="flex items-start gap-3">
      <Shield className="w-5 h-5 text-lime shrink-0 mt-0.5" />
      <div>
        <h4 className="font-inter font-bold text-lime text-sm">Privacy-First Edge Processing</h4>
        <p className="text-xs text-slate-400 mt-1">
          Video frames are converted to text metadata and <span className="text-lime font-semibold">deleted instantly</span>. 
          No footage is ever stored or transmitted. Your data stays in your kitchen.
        </p>
      </div>
    </div>
  </div>
);

// Alert Card Component
const AlertCard = ({ alert, onSpeak }) => {
  const [isSpeaking, setIsSpeaking] = useState(false);
  
  const handleSpeak = async () => {
    setIsSpeaking(true);
    try {
      await onSpeak(alert.description, alert.category.toLowerCase());
    } finally {
      setIsSpeaking(false);
    }
  };
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`rounded-lg border p-4 ${SEVERITY_COLORS[alert.severity] || SEVERITY_COLORS.INFO}`}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${
              alert.severity === "CRITICAL" ? "bg-red-500 text-white" :
              alert.severity === "WARNING" ? "bg-amber-500 text-black" :
              "bg-blue-500 text-white"
            }`}>
              {alert.severity}
            </span>
            <span className="text-xs text-slate-500">{alert.category}</span>
          </div>
          <h4 className="font-bold text-sm mb-1">{alert.title}</h4>
          <p className="text-xs text-slate-400">{alert.description}</p>
          {alert.recommended_action && (
            <p className="text-xs text-slate-300 mt-2 font-medium">
              → {alert.recommended_action}
            </p>
          )}
        </div>
        <button
          onClick={handleSpeak}
          disabled={isSpeaking}
          className="ml-3 p-2 rounded-lg hover:bg-white/10 transition-colors"
          title="Whisper Mode: Speak this alert"
        >
          {isSpeaking ? (
            <Loader2 className="w-4 h-4 animate-spin text-gold" />
          ) : (
            <Volume2 className="w-4 h-4 text-slate-400 hover:text-gold" />
          )}
        </button>
      </div>
    </motion.div>
  );
};

// Analysis Result Component
const AnalysisResult = ({ result, onSpeak }) => {
  if (!result) return null;
  
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="glass-card rounded-xl border border-gold/30 p-6"
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-gold/20 flex items-center justify-center">
            <Eye className="w-5 h-5 text-gold" />
          </div>
          <div>
            <h3 className="font-inter font-bold text-white">Analysis Complete</h3>
            <p className="text-xs text-slate-400">{result.analysis_type} | {result.processing_time_ms}ms</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <CheckCircle2 className="w-4 h-4 text-lime" />
          <span className="text-xs text-lime">Privacy Compliant</span>
        </div>
      </div>
      
      {/* Summary */}
      <div className="bg-indigo-700/30 rounded-lg p-3 mb-4">
        <p className="text-sm text-slate-200">{result.summary}</p>
      </div>
      
      {/* Alerts */}
      {result.alerts && result.alerts.length > 0 && (
        <div className="space-y-3 mb-4">
          <h4 className="text-sm font-bold text-slate-300">Alerts ({result.alerts.length})</h4>
          {result.alerts.map((alert, idx) => (
            <AlertCard key={idx} alert={alert} onSpeak={onSpeak} />
          ))}
        </div>
      )}
      
      {/* Generated SOP (for plating) */}
      {result.sop_generated && (
        <div className="border border-gold/30 rounded-lg p-4 mt-4">
          <h4 className="text-sm font-bold text-gold mb-2">Generated Plating Guide</h4>
          <p className="text-xs text-slate-400 mb-2">{result.sop_generated.title}</p>
          <ol className="text-xs text-slate-300 space-y-1">
            {result.sop_generated.steps?.slice(0, 5).map((step, idx) => (
              <li key={idx}>{idx + 1}. {step}</li>
            ))}
          </ol>
        </div>
      )}
      
      {/* Raw Metadata (collapsible) */}
      <details className="mt-4">
        <summary className="text-xs text-slate-500 cursor-pointer hover:text-slate-300">
          View raw metadata
        </summary>
        <pre className="mt-2 p-3 bg-black/30 rounded text-[10px] text-slate-400 overflow-auto max-h-40">
          {JSON.stringify(result.metadata, null, 2)}
        </pre>
      </details>
    </motion.div>
  );
};

// Main Vision Dashboard Component
export default function VisionDashboard() {
  const { token } = useAuth();
  const [selectedType, setSelectedType] = useState("plating");
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [context, setContext] = useState("");
  const [generateSop, setGenerateSop] = useState(true);
  const [recentAlerts, setRecentAlerts] = useState([]);
  const [stats, setStats] = useState(null);
  const fileInputRef = useRef(null);
  const audioRef = useRef(null);
  
  // Fetch stats and recent alerts
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, alertsRes] = await Promise.all([
          axios.get(`${API}/vision/stats`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API}/vision/alerts?limit=10`, { headers: { Authorization: `Bearer ${token}` } })
        ]);
        setStats(statsRes.data);
        setRecentAlerts(alertsRes.data.alerts || []);
      } catch (err) {
        console.error("Failed to fetch vision data:", err);
      }
    };
    if (token) fetchData();
  }, [token]);
  
  // Handle image selection
  const handleImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    if (!file.type.match(/^image\/(jpeg|png|webp)$/)) {
      toast.error("Please select a JPEG, PNG, or WebP image");
      return;
    }
    
    // Convert to base64
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = reader.result.split(",")[1];
      setImagePreview({ file, base64, url: reader.result });
    };
    reader.readAsDataURL(file);
  };
  
  // Analyze image
  const handleAnalyze = async () => {
    if (!imagePreview?.base64) {
      toast.error("Please select an image first");
      return;
    }
    
    setIsAnalyzing(true);
    setAnalysisResult(null);
    
    try {
      const res = await axios.post(
        `${API}/vision/analyze`,
        {
          image_base64: imagePreview.base64,
          analysis_type: selectedType,
          context: context || undefined,
          generate_sop: selectedType === "plating" && generateSop
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setAnalysisResult(res.data);
      toast.success("Analysis complete!");
      
      // Speak critical alerts
      if (res.data.alerts?.some(a => a.severity === "CRITICAL")) {
        const criticalAlert = res.data.alerts.find(a => a.severity === "CRITICAL");
        handleWhisperSpeak(criticalAlert.description, "safety");
      }
      
    } catch (err) {
      toast.error(err.response?.data?.detail || "Analysis failed");
    } finally {
      setIsAnalyzing(false);
    }
  };
  
  // Whisper Mode: Convert text to speech
  const handleWhisperSpeak = async (text, alertType = "general") => {
    try {
      const res = await axios.post(
        `${API}/whisper/speak`,
        { message: text, alert_type: alertType },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (res.data.audio_data) {
        // Play audio
        const audioBlob = new Blob(
          [Uint8Array.from(atob(res.data.audio_data), c => c.charCodeAt(0))],
          { type: "audio/mp3" }
        );
        const audioUrl = URL.createObjectURL(audioBlob);
        
        if (audioRef.current) {
          audioRef.current.src = audioUrl;
          audioRef.current.play();
        }
      }
    } catch (err) {
      console.error("Whisper speak failed:", err);
    }
  };
  
  // Clear analysis
  const handleClear = () => {
    setImagePreview(null);
    setAnalysisResult(null);
    setContext("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };
  
  return (
    <div className="min-h-screen bg-[#040F49]" data-testid="vision-dashboard">
      <div className="noise-overlay" />
      
      {/* Hidden audio element for Whisper Mode */}
      <audio ref={audioRef} className="hidden" />
      
      {/* Header */}
      <div className="sticky top-0 z-40 backdrop-blur-xl bg-indigo-700/90 border-b border-gold/20">
        <div className="max-w-6xl mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <ShieldLogo size="sm" />
            <span className="font-inter text-sm font-bold text-gold tracking-wider">ARGUS VISION</span>
            <span className="px-2 py-0.5 rounded-full bg-lime/20 text-lime text-[10px] font-bold uppercase">
              The Eyes
            </span>
          </div>
          <div className="flex items-center gap-3">
            {stats && (
              <div className="text-xs text-slate-400">
                <span className="text-gold font-bold">{stats.total_analyses}</span> analyses
              </div>
            )}
            <a href="/admin/founding-50" className="text-slate-400 hover:text-white text-sm">
              Dashboard
            </a>
            <a href="/" className="text-slate-400 hover:text-white">
              <Home className="w-5 h-5" />
            </a>
          </div>
        </div>
      </div>
      
      <div className="max-w-6xl mx-auto px-6 py-8">
        <PrivacyNotice />
        
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Analysis Panel */}
          <div className="lg:col-span-2 space-y-6">
            {/* Analysis Type Selector */}
            <div className="glass-card rounded-xl border border-gold/20 p-6">
              <h2 className="font-inter font-bold text-white mb-4">Select Analysis Type</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {Object.entries(ANALYSIS_TYPES).map(([key, type]) => {
                  const Icon = type.icon;
                  const isSelected = selectedType === key;
                  return (
                    <button
                      key={key}
                      onClick={() => setSelectedType(key)}
                      className={`p-4 rounded-lg border transition-all text-left ${
                        isSelected 
                          ? "border-gold bg-gold/10" 
                          : "border-slate-700 hover:border-slate-500"
                      }`}
                      data-testid={`analysis-type-${key}`}
                    >
                      <Icon className={`w-5 h-5 mb-2 ${isSelected ? "text-gold" : "text-slate-400"}`} />
                      <h4 className={`text-sm font-bold ${isSelected ? "text-white" : "text-slate-300"}`}>
                        {type.name}
                      </h4>
                      <p className="text-[10px] text-slate-500 mt-1">{type.description}</p>
                    </button>
                  );
                })}
              </div>
            </div>
            
            {/* Image Upload */}
            <div className="glass-card rounded-xl border border-gold/20 p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-inter font-bold text-white">Upload Image</h2>
                {imagePreview && (
                  <button onClick={handleClear} className="text-slate-400 hover:text-white text-sm">
                    Clear
                  </button>
                )}
              </div>
              
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={handleImageSelect}
                className="hidden"
                data-testid="image-upload-input"
              />
              
              {!imagePreview ? (
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="w-full h-48 border-2 border-dashed border-slate-600 rounded-xl hover:border-gold/50 transition-colors flex flex-col items-center justify-center gap-3"
                >
                  <Upload className="w-10 h-10 text-slate-500" />
                  <span className="text-sm text-slate-400">Click to upload JPEG, PNG, or WebP</span>
                  <span className="text-xs text-slate-600">Images are analyzed and deleted instantly</span>
                </button>
              ) : (
                <div className="relative">
                  <img
                    src={imagePreview.url}
                    alt="Preview"
                    className="w-full h-48 object-cover rounded-lg"
                  />
                  <div className="absolute top-2 right-2 flex gap-2">
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      className="p-2 bg-black/50 rounded-lg hover:bg-black/70"
                    >
                      <RefreshCw className="w-4 h-4 text-white" />
                    </button>
                  </div>
                </div>
              )}
              
              {/* Context input */}
              <div className="mt-4">
                <Label className="text-slate-300 text-xs">Context (optional)</Label>
                <Input
                  value={context}
                  onChange={(e) => setContext(e.target.value)}
                  placeholder={`e.g., ${selectedType === "plating" ? "Grilled Salmon entree" : "Walk-in cooler"}`}
                  className="mt-1 bg-indigo-700/50 border-slate-600 text-white"
                />
              </div>
              
              {/* Generate SOP toggle (plating only) */}
              {selectedType === "plating" && (
                <label className="flex items-center gap-2 mt-4 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={generateSop}
                    onChange={(e) => setGenerateSop(e.target.checked)}
                    className="w-4 h-4 rounded border-slate-600"
                  />
                  <span className="text-sm text-slate-300">Generate Plating Guide SOP</span>
                </label>
              )}
              
              {/* Analyze Button */}
              <Button
                onClick={handleAnalyze}
                disabled={!imagePreview || isAnalyzing}
                className="w-full mt-4 bg-gold hover:bg-gold/80 text-indigo-700 font-bold"
                data-testid="analyze-btn"
              >
                {isAnalyzing ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    Analyzing (Privacy-First)...
                  </>
                ) : (
                  <>
                    <Eye className="w-4 h-4 mr-2" />
                    Analyze Image
                  </>
                )}
              </Button>
            </div>
            
            {/* Analysis Result */}
            {analysisResult && (
              <AnalysisResult result={analysisResult} onSpeak={handleWhisperSpeak} />
            )}
          </div>
          
          {/* Sidebar: Recent Alerts */}
          <div className="space-y-6">
            <div className="glass-card rounded-xl border border-gold/20 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-inter font-bold text-white">Recent Alerts</h3>
                <span className="text-xs text-slate-500">{recentAlerts.length} alerts</span>
              </div>
              
              {recentAlerts.length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-8">
                  No alerts yet. Start analyzing images!
                </p>
              ) : (
                <div className="space-y-3 max-h-[600px] overflow-y-auto">
                  {recentAlerts.slice(0, 8).map((alert, idx) => (
                    <AlertCard key={idx} alert={alert} onSpeak={handleWhisperSpeak} />
                  ))}
                </div>
              )}
            </div>
            
            {/* Quick Stats */}
            {stats && (
              <div className="glass-card rounded-xl border border-gold/20 p-6">
                <h3 className="font-inter font-bold text-white mb-4">Vision Stats</h3>
                <div className="space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-400">Total Analyses</span>
                    <span className="text-gold font-bold">{stats.total_analyses}</span>
                  </div>
                  {Object.entries(stats.by_type || {}).map(([type, count]) => (
                    <div key={type} className="flex justify-between text-xs">
                      <span className="text-slate-500">{ANALYSIS_TYPES[type]?.name || type}</span>
                      <span className="text-slate-300">{count}</span>
                    </div>
                  ))}
                  <div className="pt-3 border-t border-slate-700">
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-400">Privacy Compliance</span>
                      <span className="text-lime font-bold">{stats.privacy_compliance}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            {/* BYOD Info */}
            <div className="glass-card rounded-xl border border-slate-700 p-6">
              <h3 className="font-inter font-bold text-white mb-3">Staff BYOD</h3>
              <p className="text-xs text-slate-400 mb-3">
                Your team uses their own devices. Alerts go directly to:
              </p>
              <ul className="text-xs text-slate-300 space-y-2">
                <li className="flex items-center gap-2">
                  <Volume2 className="w-3 h-3 text-gold" />
                  Whisper Mode (Voice in ear)
                </li>
                <li className="flex items-center gap-2">
                  <Zap className="w-3 h-3 text-gold" />
                  Silent Alert (Haptic on wrist)
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
